<template>
    <b-container fluid>
        <b-row class="mb-4 mb-md-4 mb-xl-5">
            <b-col cols lg="12" xl="4" class="mb-4 mb-md-4 mb-xl-0">
                <b-row class="h-100">
                    <b-col cols="12" md="6" xl="12" class="mb-4 mb-md-0 mb-xl-0">
                        <qesg-dash-card
                            :title="$t('총 참여기업')"
                            :index="`${stats.total} ${$i18n.locale == 'ko' ? '개 기업': ''}`"
                            icon="building"
                            icon-variant="info"
                        />
                    </b-col>
                    <b-col cols="12" md="6" xl="12" class="align-self-md-end">
                        <qesg-dash-card
                            :title="$t('총 평가완료 기업')"
                            :index="`${stats.fields['완료됨']} ${$i18n.locale == 'ko' ? '개 기업': ''}`"
                            icon="clipboard-check"
                            iconVariant="primary"
                        />
                    </b-col>
                </b-row>
            </b-col>
            <b-col cols lg="12" xl="8" class="">
                <qesg-dash-chart-card
                    icon="clipboard-data"
                    icon-variant="info"
                >
                    <template v-slot:header-title>
                        <h5 class="dash-card-title mb-0">
                            {{ $t('총 참가기업 현황') }}
                            <b-icon-info-circle v-b-popover.hover.focus="statsPopoverConfig" variant="secondary" scale="0.7"
                                                class="ml-0 mb-1" tabindex="0" :aria-label="$t('진행현황 상태 도움말')"/>
                        </h5>
                    </template>

                    <b-row class="justify-content-xl-around py-2">
                        <b-col cols md="5" lg="5" xl="5">
                            <loading-view
                                :is-loading="isLoadProjectData"
                                size="sm"
                                variant="dark"
                            >
                                <h6 class="font-italic font-weight-normal"> {{ $t('대기중') }}... </h6>
                            </loading-view>
                            <div class="chart-container mb-3 mb-md-0"
                                 style="height: 235px;"
                            >
                                <canvas id="progress-pie-chart"/>
                            </div>
                        </b-col>
                        <b-col cols md="7" lg="6" xl="7" sl="5">
                            <b-row id="chart-progress-legend"
                                   class="align-items-center justify-content-center justify-content-xl-end h-100 pr-lg-5 pr-xl-5">
                                <b-col v-for="(value, key, index) in stats.fields"
                                       :key="`progress-stats-${index}`"
                                       cols="6" md="6"
                                       :class="[index > 1 ? 'mt-4 mt-md-3' : 'mt-4 mt-md-0', 'user-select-none']"
                                >
                                    <!-- {{ index }} -->
                                    <div class="d-flex align-items-center mb-1">
                                        <div class="rounded mr-2"
                                             :style="{ width: '17px', height: '17px', backgroundColor: stats.colors[key] }"
                                        />
                                        <div class="">
                                            {{ $t(key) }}:
                                        </div>
                                        <div class="flex-grow-1 text-right">
                                            {{ value }} {{ $i18n.locale == 'ko' ? '개' : '' }}
                                        </div>
                                    </div>
                                    <div class="">
                                        <b-btn
                                            variant="light"
                                            size="sm"
                                            :title="$t('{status} 상태의 기업 보기', {status: $t(key)})"
                                            :aria-label="$t('{status} 상태의 기업 보기', {status: $t(key)})"
                                            :disabled="isParticipantsTableBusy"
                                            block
                                            @click="viewParticipantByLegend(key)"
                                        >
                                            <BIconSearch font-scale="0.9" shift-v="3" class="ml-1"/>
                                        </b-btn>
                                    </div>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </qesg-dash-chart-card>
            </b-col>
        </b-row>
        <b-row class="mb-4 mb-md-4 mb-xl-5">
            <b-col cols="12">
                <qesg-dash-card-wrapper no-body>
                    <b-row align-v="center" no-gutters class="border-bottom border-color-gray-300 px-3 mb-3 pt-2 pb-3">
                        <b-col cols>
                            <h5 class="dash-card-title mb-0">{{ $t('프로젝트 기간') }}</h5>
                        </b-col>
                        <b-col cols="auto">
                            <div class="d-flex">
                                <qesg-dash-card-icon icon="calendar3" variant="secondary"/>
                            </div>
                        </b-col>
                    </b-row>

                    <b-list-group horizontal="md">
                        <b-list-group-item class="d-flex align-items-center flex-grow-1 border-0">
                            <div class="d-block d-md-none d-lg-block mr-2">
                                <qesg-dash-card-icon icon="calendar-check" variant="info" :font-scale="2" class="mr-2"/>
                            </div>

                            <div class="d-inline-block">
                                <div class="d-none d-md-inline-flex d-lg-none">
                                    <qesg-dash-card-icon icon="calendar-check" variant="info" :font-scale="0.9"
                                                         class="d-none d-md-flex d-lg-none mr-2"/>
                                </div>

                                <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">
                                    {{ $t('목표 기간') }}</h6>
                                <h4 v-if="$i18n.locale === 'ko'"
                                    class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.target_end_date_dis) }}</h4>
                                <h4 v-else class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.target_end_date_dis_en) }}</h4>
                            </div>
                        </b-list-group-item>
                        <b-list-group-item class="d-flex align-items-center flex-grow-1 border-0">
                            <div class="d-block d-md-none d-lg-block mr-2">
                                <qesg-dash-card-icon icon="calendar4-Event" variant="info" :font-scale="2"
                                                     class="mr-2"/>
                            </div>

                            <div class="d-inline-block">
                                <div class="d-none d-md-inline-flex d-lg-none">
                                    <qesg-dash-card-icon icon="calendar4-Event" variant="info" :font-scale="0.9"
                                                         class="d-none d-md-flex d-lg-none mr-2"/>
                                </div>

                                <!-- <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">{{ $t('프로젝트 코드') }} {{ $t('시작일') }}</h6> -->
                                <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">
                                    {{ $t('프로젝트') }} {{ $t('시작일') }}
                                </h6>
                                <h4 v-if="$i18n.locale === 'ko'"
                                    class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.start_date_dis) }}</h4>
                                <h4 v-else class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.start_date_dis_en) }}</h4>
                            </div>

                        </b-list-group-item>
                        <b-list-group-item class="d-flex align-items-center flex-grow-1 border-0">
                            <div class="d-block d-md-none d-lg-block mr-2">
                                <qesg-dash-card-icon icon="calendar4-week" variant="info" :font-scale="2" class="mr-2"/>
                            </div>

                            <div class="d-inline-block">
                                <div class="d-none d-md-inline-flex d-lg-none">
                                    <qesg-dash-card-icon icon="calendar4-week" variant="info" :font-scale="0.9"
                                                         class="d-none d-md-flex d-lg-none mr-2"/>
                                </div>

                                <h6 class="d-block d-md-inline-block d-lg-block dash-card-title text-secondary mb-0">
                                    {{ $t('프로젝트') }} {{ $t('종료일') }}
                                </h6>
                                <h4 v-if="$i18n.locale === 'ko'"
                                    class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.end_date_dis) }}</h4>
                                <h4 v-else class="d-inline-block d-md-block d-lg-inline-block mb-0">
                                    {{ $t(projectDates.end_date_dis_en) }}</h4>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </qesg-dash-card-wrapper>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="12">
                <qesg-dash-table-card class="h-100" icon="table">
                    <template v-slot:title>
                        <h5 class="d-inline-block font-weight-normal mb-0 mr-2">
                            <span class="text-secondary">{{ $t('기업별 진행현황') }}</span>
                            <b-badge id="popover-participant-count" variant="info" class="text-white ml-2" pill tabindex="0">
                                <small>{{ $t('표시 {shown} / 전체 {total}', {shown: currentParticipantsCount.current, total: currentParticipantsCount.total}) }}</small>
                            </b-badge>
                        </h5>
                        <b-popover target="popover-participant-count" triggers="hover focus">
                            <template #title>{{ $t('기업별 진행현황 도움말') }}</template>
                            {{ $t('앞의 숫자는 지금 표에 표시된(조건에 맞는) 기업 수, 뒤의 숫자는 프로젝트 전체 기업 수입니다.') }}
                        </b-popover>
                    </template>
                    <b-row no-gutters class="border-top border-bottom border-color-gray-300 py-3 px-3">
                        <b-col cols="12" md="6" lg="4">
                            <div class="d-flex align-items-center justify-content-end">
                                <b-input-group>
                                    <b-form-input v-model="participnatfilterForm.company_name"
                                                  :title="$t('참여기업 이름 검색')"
                                                  :aria-label="$t('참여기업 이름 검색')"
                                                  :placeholder="$t('참여기업 이름을 입력해주세요')"
                                                  trim
                                                  @keyup.enter="searchParticipantByName"
                                    />
                                    <b-input-group-append>
                                        <b-btn
                                            :title="$t('참여기업 이름 검색')"
                                            :aria-label="$t('참여기업 이름 검색')"
                                            :disabled="isParticipantsTableBusy"
                                            @click="searchParticipantByName"
                                        >
                                            <BIconSearch/>
                                        </b-btn>
                                        <b-btn
                                            variant="light"
                                            class="border"
                                            :disabled="isParticipantsTableBusy"
                                            :title="$t('조건 검색')"
                                            :aria-label="$t('조건 검색')"
                                            :aria-expanded="collapsedParticipantFilterForm ? 'true' : 'false'"
                                            aria-controls="collapse-participant-table-filter"
                                            @click="collapsedParticipantFilterForm = !collapsedParticipantFilterForm"
                                        >
                                            <B-Icon-filter variant=""/>
                                        </b-btn>
                                    </b-input-group-append>
                                </b-input-group>
                            </div>
                        </b-col>
                        <b-col cols="12" md="6" offset-md="2" class="text-right mt-2 mt-md-0">
                            <b-btn-group>
                                <b-btn
                                    variant="light"
                                    class="border ml-auto"
                                    :disabled="isParticipantsTableBusy"
                                    :title="$t('현재 목록 엑셀 다운로드')"
                                    :aria-label="$t('현재 목록 엑셀 다운로드')"
                                    @click="downloadParticipantsExcel"
                                >
                                    <b-icon-download/>
                                </b-btn>
                                <b-btn
                                    variant="light"
                                    class="border mr-2"
                                    :disabled="isParticipantsTableBusy"
                                    :title="$t('검색 초기화')"
                                    :aria-label="$t('검색 초기화')"
                                    @click="resetParticipantFilter"
                                >
                                    <b-icon-arrow-repeat/>
                                </b-btn>
                            </b-btn-group>
                            <b-form-select
                                :value="listQuery.perPage"
                                :options="participantTablePerPageOpt"
                                :disabled="isParticipantsTableBusy"
                                :aria-label="$t('표시 개수')"
                                class="w-auto"
                                @change="perPage => navigateList({perPage})"
                            />
                        </b-col>
                    </b-row>
                    <b-collapse id="collapse-participant-table-filter" v-model="collapsedParticipantFilterForm">
                        <b-row>
                            <b-col cols>
                                <div class="border bg-light rounded p-3 m-3">
                                    <b-form
                                        @submit.prevent="onSubmitParticipantFilter"
                                        @reset.prevent="resetParticipantFilter"
                                    >
                                        <b-form-row>
                                            <b-col cols="12" md="4">
                                                <b-form-group
                                                    id="participant-filter-name"
                                                    :label="$t('참여기업명')"
                                                    label-for="input-participant-name"
                                                >
                                                    <b-form-input
                                                        id="input-participant-name"
                                                        v-model="participnatfilterForm.company_name"
                                                        type="text"
                                                        :placeholder="$t('참여기업 이름을 입력해주세요')"
                                                        :disabled="isParticipantsTableBusy"
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="8">
                                                <b-form-group
                                                    id="participant-filter-status_code"
                                                    v-slot="{ ariaDescribedby }"
                                                    :label="$t('평가참여 상태')"
                                                    label-for="checkbox-participant-status_code"
                                                >
                                                    <b-form-checkbox-group
                                                        id="checkbox-participant-status_code-group"
                                                        v-model="participnatfilterForm.status_code"
                                                        :aria-describedby="ariaDescribedby"
                                                        name="checkbox-participant-status_code-group"
                                                    >
                                                        <b-form-checkbox v-for="(val, key, s) in statsLabel"
                                                                         :key="`participant-status_code-checkbox-${s}-${val}`"
                                                                         :value="val"
                                                                         :disabled="isParticipantsTableBusy"
                                                                         inline
                                                        >
                                                            {{ $t(key) }}
                                                        </b-form-checkbox>
                                                    </b-form-checkbox-group>
                                                </b-form-group>
                                            </b-col>
                                        </b-form-row>
                                        <b-form-row>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('신청일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-requested_ts_start"
                                                    :label="`${$t('신청일 조회')} ${$t('시작일')}`"
                                                    label-for="datepicker-participant-requested_ts_start"
                                                    label-sr-only
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-requested_ts_start"
                                                        v-model="participnatfilterForm.requested_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-requested_ts_end"
                                                    :label="`${$t('신청일 조회')} ${$t('종료일')}`"
                                                    label-for="datepicker-participant-requested_ts_end"
                                                    label-sr-only
                                                >
                                                    <!-- label="신청 - 종료일" -->
                                                    <b-form-datepicker
                                                        id="datepicker-participant-requested_ts_end"
                                                        v-model="participnatfilterForm.requested_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('평가일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-in_assessment_ts_start"
                                                    :label="`${$t('평가일 조회')} ${$t('시작일')}`"
                                                    label-for="datepicker-participant-in_assessment_ts_start"
                                                    label-sr-only
                                                >
                                                    <!-- label="평가 - 시작일" -->
                                                    <b-form-datepicker
                                                        id="datepicker-participant-in_assessment_ts_start"
                                                        v-model="participnatfilterForm.in_assessment_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-in_assessment_ts_end"
                                                    :label="`${$t('평가일 조회')} ${$t('종료일')}`"
                                                    label-for="datepicker-participant-in_assessment_ts_end"
                                                    label-sr-only
                                                >
                                                    <!-- label="평가 - 종료일" -->
                                                    <b-form-datepicker
                                                        id="datepicker-participant-in_assessment_ts_end"
                                                        v-model="participnatfilterForm.in_assessment_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('완료일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-completed_ts_start"
                                                    :label="`${$t('완료일 조회')} ${$t('시작일')}`"
                                                    label-for="datepicker-participant-completed_ts_start"
                                                    label-sr-only
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-completed_ts_start"
                                                        v-model="participnatfilterForm.completed_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-completed_ts_end"
                                                    :label="`${$t('완료일 조회')} ${$t('종료일')}`"
                                                    label-for="datepicker-participant-completed_ts_end"
                                                    label-sr-only
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-completed_ts_end"
                                                        v-model="participnatfilterForm.completed_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="12" md="6" xl="3">
                                                <p class="font-weight-normal mb-2">{{ $t('마감일 조회') }}</p>
                                                <b-form-group
                                                    id="participant-filter-duedate_ts_start"
                                                    :label="`${$t('마감일 조회')} ${$t('시작일')}`"
                                                    label-for="datepicker-participant-duedate_ts_start"
                                                    label-sr-only
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-duedate_ts_start"
                                                        v-model="participnatfilterForm.duedate_ts.start"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('시작일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                                <b-form-group
                                                    id="participant-filter-duedate_ts_end"
                                                    :label="`${$t('마감일 조회')} ${$t('종료일')}`"
                                                    label-for="datepicker-participant-duedate_ts_end"
                                                    label-sr-only
                                                >
                                                    <b-form-datepicker
                                                        id="datepicker-participant-duedate_ts_end"
                                                        v-model="participnatfilterForm.duedate_ts.end"
                                                        :locale="date_picker_locale"
                                                        :placeholder="$t('종료일')"
                                                        :disabled="isParticipantsTableBusy"
                                                        today-button
                                                        reset-button
                                                        close-button
                                                    />
                                                </b-form-group>
                                            </b-col>
                                        </b-form-row>
                                        <b-form-row>
                                            <b-col cols>
                                                <hr>
                                            </b-col>
                                        </b-form-row>
                                        <b-form-row>
                                            <b-col class="text-right">
                                                <b-btn type="submit" variant="primary" class="mr-2"
                                                       :disabled="isParticipantsTableBusy">{{ $t('조회') }}
                                                </b-btn>
                                                <b-btn type="reset" variant="outline-secondary" class="mr-2"
                                                       :disabled="isParticipantsTableBusy">{{ $t('초기화') }}
                                                </b-btn>
                                                <b-button
                                                    variant="secondary"
                                                    :disabled="isParticipantsTableBusy"
                                                    @click="collapsedParticipantFilterForm = !collapsedParticipantFilterForm"
                                                >
                                                    {{ $t('닫기') }}
                                                </b-button>
                                            </b-col>
                                        </b-form-row>
                                    </b-form>
                                </div>
                            </b-col>
                        </b-row>
                    </b-collapse>
                    <p v-if="appliedFilterSummary" class="small text-secondary px-3 pt-2 mb-0">
                        {{ $t('적용된 조건') }}: {{ appliedFilterSummary }}
                    </p>
                    <b-row no-gutters>
                        <b-col cols>
                            <b-table
                                id="total-participant-table"
                                class="border-bottom border-color-gray-300"
                                thead-class="sticky-header-sort-fix"
                                :items="participants"
                                :fields="participantsFields"
                                :busy="isParticipantsTableBusy"
                                :empty-html="`<h5 class='mt-3 gray-500'>${ $t('조회된 참여기업이 없습니다.') }</h5>`"
                                details-td-class="bg-light"
                                show-empty
                                responsive
                                hover
                                sticky-header="75vh"
                                no-border-collapse
                                :per-page="listQuery.perPage"
                                :current-page="listQuery.currentPage"
                            >
                                <template #table-busy>
                                    <div class="text-center text-primary my-3">
                                        <b-spinner class="align-middle mr-3"></b-spinner>
                                        <strong> {{ participantsTableStatusStatement }}</strong>
                                    </div>
                                </template>

                                <template #empty="scope">
                                    <div class="text-center" v-html="scope.emptyHtml"></div>
                                </template>

                                <template #head(index)="data">
                                    <div class="text-center">{{ data.label }}</div>
                                </template>

                                <template #head(show_details)="data">
                                    <div class="d-inline-block text-center mr-1">{{ data.label }}</div>
                                    <b-icon-info-circle id="popover-participant-table-details" variant="secondary"
                                                        scale="1" class="ml-1" tabindex="0" :aria-label="$t('상세내용 도움말')"/>
                                    <b-popover target="popover-participant-table-details" triggers="hover focus">
                                        <template #title>{{ $t('상세내용 도움말') }}</template>
                                        <div v-if="$i18n.locale === 'ko'">
                                            아래 <strong>상세내용 보기</strong> 버튼을 클릭하시면 참여기업의 신청일, 답변일 등의 날짜와 담당자 이메일 등 상세내용을
                                            보실 수 있습니다.
                                        </div>
                                        <div v-else-if="$i18n.locale === 'en'">
                                            Click the <strong>View Details</strong> button below to see detailed
                                            information such as application dates, response dates, and manager's email
                                            for the participating company.
                                        </div>
                                        <div v-else>
                                            点击下面的 <strong>查看详细信息</strong>
                                            按钮，您可以查看参与公司的申请日期、回答日期等详细信息，以及负责人的电子邮件。
                                        </div>
                                    </b-popover>
                                </template>

                                <template #cell(index)="data">
                                    <div class="text-center">
                                        {{ (listQuery.currentPage - 1) * listQuery.perPage + data.index + 1 }}
                                    </div>
                                </template>

                                <template #cell(status_code)="data">
                                    <div class="d-inline px-2 py-1 rounded-pill small"
                                         :style="[{backgroundColor: stats.colors[statsLabel_eng[data.value]], color: statsTextColors[statsLabel_eng[data.value]] || '#fff'}]"
                                    >
                                        {{ $t(statsLabel_eng[data.value]) }}
                                    </div>
                                </template>


                                <template #cell(service_type)="data">
                                    {{ data.value ? $t(`${data.value} 평가`) : '-' }}
                                </template>

                                <template #cell(completed_rate)="data">
                                    <div class="" style="width: 200px;">
                                        <b-progress :max="100" height="1.4rem">
                                            <b-progress-bar
                                                :value="data.item.status_code === 'completed' ? 100 : Number((data.value * 100).toFixed(1))"
                                                :label="`${data.item.status_code === 'completed' ? Number(100).toFixed(1) : Number(data.value * 100).toFixed(1)} %`"
                                                :style="[{ backgroundColor: data.item.status_code === 'completed' ? `${stats.colors['완료됨']} !important` : `${stats.colors[data.item.status_code]} !important` }]"
                                            >
                                            </b-progress-bar>
                                        </b-progress>
                                    </div>
                                </template>

                                <template #cell(show_details)="row">
                                    <b-btn size="sm" class="mr-2" :aria-expanded="row.detailsShowing ? 'true' : 'false'" @click="row.toggleDetails">
                                        {{ $t('상세') }}
                                        <fa v-if="row.detailsShowing" icon="chevron-up"/>
                                        <fa v-else icon="chevron-down"/>
                                    </b-btn>
                                </template>

                                <template #row-details="row">
                                    <div class="d-flex border rounded bg-white mb-3">
                                        <div class="flex-grow-1 p-3">
                                            <p class="font-weight-normal mb-2">{{ $t('담당자 이메일') }}: </p>
                                            <h6 class="d-inline">{{ row.item.requested_e_mail }}</h6>
                                        </div>
                                    </div>
                                    <b-list-group horizontal="md">
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('신청일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.requested_ts">
                                                        <!-- --gray-600 -->
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.requested_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.requested_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.requested_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.requested_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('답변 시작일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.in_progress_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.in_progress_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.in_progress_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.in_progress_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.in_progress_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('답변 종료 예정일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.duedate_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.duedate_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.duedate_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.duedate_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.duedate_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('평가일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.in_assessment_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.in_assessment_ts_dis.date }}</span><br>
                                                            <span>{{ row.item.in_assessment_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.in_assessment_ts_dis_en.date }}</span>
                                                            <br>
                                                            <span>{{ row.item.in_assessment_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="flex-grow-1">
                                            <div style="width: 135px">
                                                <p class="font-weight-normal mb-2">{{ $t('완료일') }}: </p>
                                                <h6 class="d-inline">
                                                    <template v-if="!row.item.completed_ts">
                                                        -
                                                    </template>
                                                    <template v-else>
                                                        <template v-if="$i18n.locale === 'ko'">
                                                            <span>{{ row.item.completed_ts_dis.date }}</span> <br>
                                                            <span>{{ row.item.completed_ts_dis.time }}</span>
                                                        </template>
                                                        <template v-else>
                                                            <span>{{ row.item.completed_ts_dis_en.date }}</span> <br>
                                                            <span>{{ row.item.completed_ts_dis_en.time }}</span>
                                                        </template>
                                                    </template>
                                                </h6>
                                            </div>
                                        </b-list-group-item>
                                    </b-list-group>
                                </template>
                            </b-table>
                            <b-row no-gutters class="align-items-center justify-content-center mb-3 px-4">
                                <b-col cols="auto">
                                    <!-- 목록·표시 개수가 바뀌면 다시 만든다: one-way :value 라 b-pagination 내부 보정(빈 목록에서 1페이지)이 URL 페이지와 어긋나지 않게 -->
                                    <b-pagination
                                        :key="`${participants.length}-${listQuery.perPage}`"
                                        :value="listQuery.currentPage"
                                        :total-rows="participants.length"
                                        :per-page="listQuery.perPage"
                                        :disabled="isParticipantsTableBusy"
                                        aria-controls="total-participant-table"
                                        class="dash-pagination mb-0"
                                        @change="page => navigateList({currentPage: page})"
                                    />
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                </qesg-dash-table-card>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import dayjs from 'dayjs';
import {mapGetters, mapMutations} from 'vuex';
import {BIconArrowRepeat, BIconFilter, BIconSearch} from 'bootstrap-vue';
import initializeDataProgress from '@shared_mixins/contractor-dashboard/initialize-data-progress';
import transformParticipantsList from '@shared_mixins/contractor-dashboard/transformParticipantsList.js';
import {
    readContractorListQuery, buildContractorListQuery, isSameQuery, hasListFilters, isSameListFilters, toSearchCondition
} from '@shared_mixins/contractor-dashboard/contractor-list-query';
import downloadFile from '@shared_mixins/downloadFile';
import QesgDashCard from '@shared_components/contractor/components/qesg-dash-card';
import QesgDashChartCard from '@shared_components/contractor/components/qesg-dash-chart-card';
import QesgDashCardWrapper from '@shared_components/contractor/components/qesg-dash-card-wrapper.vue';
import QesgDashCardIcon from '@shared_components/contractor/components/qesg-dash-card-icon.vue';
import QesgDashTableCard from '@shared_components/contractor/components/qesg-dash-table-card.vue';
import loadingView from '@shared_components/common/loading-view.vue';

// URL 에 남기는 목록 상태 (?company_name&status_code&requested_ts_start&...&current_page&per_page)
const PARTICIPANT_LIST_SPEC = Object.freeze({
    fields: {
        company_name: 'string',
        status_code: {
            type: 'array',
            values: ['not_requested', 'requested', 'in_progress', 're_progress', 'in_assessment', 'completed', 'canceled', 'eco_vadis']
        },
        requested_ts: 'date_range',
        in_progress_ts: 'date_range',
        in_assessment_ts: 'date_range',
        completed_ts: 'date_range',
        duedate_ts: 'date_range'
    },
    perPageOptions: [50, 75, 100, 150, 200],
    defaultPerPage: 100
});

const DATE_FILTER_LABELS = Object.freeze({
    requested_ts: '신청일',
    in_progress_ts: '답변 시작일',
    in_assessment_ts: '평가일',
    completed_ts: '완료일',
    duedate_ts: '답변 종료 예정일'
});

export default {
    name: 'contractor-progress-v2',
    props: {
        loadedRequiredData: {
            required: true,
            type: Boolean
        }
    },
    mixins: [
        downloadFile,
        initializeDataProgress,
        transformParticipantsList
    ],
    components: {
        BIconArrowRepeat,
        BIconFilter,
        BIconSearch,
        loadingView,
        QesgDashCard,
        QesgDashChartCard,
        QesgDashCardWrapper,
        QesgDashCardIcon,
        QesgDashTableCard
    },
    data() {
        return {
            isLoadProjectData: false,
            statsChartInstance: null,
            // 입력 중인 조건(조회 전). 적용된 조건은 URL 과 appliedFilters
            participnatfilterForm: readContractorListQuery({}, PARTICIPANT_LIST_SPEC).filters,
            participants: [],
            // 표에 보이는 목록의 조건과 요청 params (엑셀은 이 params 로 받는다)
            appliedFilters: null,
            latest_search_params: {filter: 'all'},
            isParticipantSearching: false,
            isParticipantDownloading: false,
            collapsedParticipantFilterForm: false,
            // 프로젝트 전환·뒤로가기로 늦게 도착한 이전 응답을 버린다
            listRequestSeq: 0
        };
    },
    computed: {
        ...mapGetters('contractor', ['Get_Contractor', 'GET_ProjectList']),
        date_picker_locale() {
            if (this.$i18n.locale === 'ca') {
                return 'zh-cn';
            }
            return this.$i18n.locale;
        },
        listQuery() {
            return readContractorListQuery(this.$route.query, PARTICIPANT_LIST_SPEC);
        },
        participantsFields() {
            return [
                {
                    key: 'index',
                    label: this.$t('번호'),
                    stickyColumn: true,
                    sortable: false,
                    thClass: 'h6 mb-0',
                    thStyle: {
                        width: '80px'
                    },
                },
                {
                    key: 'company_name',
                    label: this.$t('참여기업명'),
                    stickyColumn: true,
                    sortable: true,
                    thClass: 'h6 mb-0'
                },
                {
                    key: 'status_code',
                    label: this.$t('상태'),
                    sortable: true,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: this.$i18n.locale === 'en' ? '150px' : '100px'
                    },
                    tdClass: 'text-center',
                },
                {
                    key: 'completed_rate',
                    label: this.$t('진행률'),
                    sortable: true,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: '224px'
                    },
                    tdClass: 'text-center',
                },
                {
                    key: 'show_details',
                    label: this.$t('상세'),
                    sortable: false,
                    thClass: 'h6 mb-0 text-center',
                    thStyle: {
                        width: '120px'
                    },
                    tdClass: 'text-center',
                }
            ];
        },
        participantTablePerPageOpt() {
            if (this.$i18n.locale === 'ko') {
                return [
                    {text: '50개씩 보기', value: 50},
                    {text: '75개씩 보기', value: 75},
                    {text: '100개씩 보기', value: 100},
                    {text: '150개씩 보기', value: 150},
                    {text: '200개씩 보기', value: 200}
                ];
            } else if (this.$i18n.locale === 'en') {
                return [
                    {text: 'View 50 per page', value: 50},
                    {text: 'View 75 per page', value: 75},
                    {text: 'View 100 per page', value: 100},
                    {text: 'View 150 per page', value: 150},
                    {text: 'View 200 per page', value: 200}
                ];
            } else {
                return [
                    {text: '每页显示50条', value: 50},
                    {text: '每页显示75条', value: 75},
                    {text: '每页显示100条', value: 100},
                    {text: '每页显示150条', value: 150},
                    {text: '每页显示200条', value: 200}
                ];
            }
        },
        apiRequestProjectCode() {
            return this.$route.query.projectid;
        },
        isParticipantsTableBusy() {
            return this.isParticipantSearching || this.isLoadProjectData || this.isParticipantDownloading;
        },
        participantsTableStatusStatement() {
            if (this.isParticipantSearching) {
                return this.$t('참여기업 리스트를 조회 중입니다.');
            } else if (this.isParticipantDownloading) {
                return this.$t('참여기업 리스트 엑셀파일을 다운로드 중입니다.');
            } else {
                return this.$t('참여기업 리스트를 불러오는 중입니다.');
            }
        },
        // 표 위에 보여 줄 적용 조건 한 줄 (조건이 없으면 빈 문자열)
        appliedFilterSummary() {
            const _filters = this.appliedFilters;
            if (!_filters || !hasListFilters(PARTICIPANT_LIST_SPEC, _filters)) return '';
            const _parts = [];
            if (_filters.company_name) {
                _parts.push(`${this.$t('참여기업명')}: ${_filters.company_name}`);
            }
            if (_filters.status_code?.length) {
                _parts.push(_filters.status_code.map(_code => this.$t(this.statsLabel_eng[_code])).join(', '));
            }
            Object.entries(DATE_FILTER_LABELS).forEach(([_key, _label]) => {
                const {start, end} = _filters[_key] || {};
                if (start && end) {
                    _parts.push(`${this.$t(_label)} ${start} ~ ${end}`);
                } else if (start) {
                    _parts.push(`${this.$t(_label)} ${this.$t('{date} 이후', {date: start})}`);
                } else if (end) {
                    _parts.push(`${this.$t(_label)} ${this.$t('{date} 이전', {date: end})}`);
                }
            });
            return _parts.join(' · ');
        },
        statsPopoverConfig() {
            return {
                html: true,
                title: () => {
                    return this.$t('진행현황 상태 도움말');
                },
                content: () => {
                    // Note this is called only when the popover is opened
                    return `
                        <div>
                            <div> <span class="mr-2">${this.$t('미신청')}: ${this.$t('ESG 평가 미신청')}</span> </div>
                            <div> <span class="mr-2">${this.$t('신청함')}: ${this.$t('ESG 평가 신청')}</span> </div>
                            <div> <span class="mr-2">${this.$t('답변중')}: ${this.$t('ESG 평가 질문지 답변중')}</span> </div>
                            <div> <span class="mr-2">${this.$t('보완중')}: ${this.$t('ESG 평가 질문지 보완 답변중')}</span> </div>
                            <div> <span class="mr-2">${this.$t('평가중')}: ${this.$t('ESG 답변 평가중')}</span> </div>
                            <div> <span class="mr-2">${this.$t('완료됨')}: ${this.$t('ESG 평가 완료')}</span> </div>
                            <div> <span class="mr-2">${this.$t('취소됨')}: ${this.$t('ESG 평가 취소')}</span> </div>
                            <div> <span class="mr-2">${this.$t('에코바디스')}: "${this.$t('에코바디스 평가사')}" ${this.$t('ESG 평가로 대체')}</span> </div>
                        </div>
                    `;
                }
            };
        },
        currentParticipantsCount() {
            const _project = this.GET_ProjectList.find(_project => _project.project_code === this.apiRequestProjectCode);
            return {total: _project?.project_company_num ?? 0, current: this.participants.length};
        }
    },
    watch: {
        // 같은 화면에서 query 만 바뀌어도(뒤로가기·링크·내 push) 목록을 URL 에 맞춘다
        '$route.query'(_query, _old_query) {
            if (!this.loadedRequiredData) return;
            if (_query.projectid !== _old_query?.projectid) {
                this.collapsedParticipantFilterForm = false;
                this.loadProject();
                return;
            }
            // 전체 조회가 진행 중이면 그 조회가 끝날 때 최신 query 를 반영한다
            if (this.isLoadProjectData) return;
            this.applyListQuery();
        },
        isLoadProjectData(status) {
            if (status) {
                this.stats.total = 0;
                this.stats.fields = {'신청함': 0, '답변중': 0, '보완중': 0, '평가중': 0, '완료됨': 0, '취소됨': 0, '미신청': 0, '에코바디스': 0};
            }

            this.$nextTick(() => {
                if (!status) {
                    this.drawPieChart();

                }
            });
        },
        '$i18n.locale'() {
            this.drawPieChart();
        },
        loadedRequiredData: {
            async handler(_state, _old_state) {
                if (_state && !_old_state) {
                    await this.loadProject();
                }
            }
        }
    },
    created() {
        this.participants = [];
        this.$emit('emit-loaded-status', false);
    },
    async mounted() {
        this.$nextTick(() => {
            this.drawPieChart();
        });

        if (this.loadedRequiredData) {
            await this.loadProject();
        }

        this.$nextTick(() => {
            const totalParticipantTable = document.getElementById('total-participant-table');
            if (totalParticipantTable) {
                totalParticipantTable.parentElement.classList.add('b-table-sticky-header-sm');
            }
        });
    },
    beforeDestroy() {
        const totalParticipantTable = document.getElementById('total-participant-table');
        if (totalParticipantTable) {
            totalParticipantTable.parentElement.classList.remove('b-table-sticky-header-sm');
        }
    },
    methods: {
        ...mapMutations('contractor', ['SET_PROJECT_LIST', 'SET_CONTRACTOR_REQUEST_TIME']),
        // 응답이 없는 오류(네트워크 단절·타임아웃)도 안내한다
        emitLoadError(error) {
            const _status = error?.response?.status === 401 ? 'not-allowed' : 'not-recognized';
            this.$emit('emit-error-status', {status: true, message: _status});
        },
        // 통계·기간·전체 목록을 받은 뒤 URL 의 조건을 적용한다
        async loadProject() {
            if (await this.requestProjectProgress()) {
                await this.applyListQuery();
            }
        },
        async requestProjectProgress() {
            const _seq = ++this.listRequestSeq;
            this.isParticipantSearching = false;
            this.isLoadProjectData = true;
            this.$emit('emit-loaded-status', false);
            try {
                const { data: _res } = await this.$projectsAxios.get(`/${this.apiRequestProjectCode}/overall`);
                if (_seq !== this.listRequestSeq) return false;

                const _res_stats = _res?.stats ?? {};
                const _fields = Object.keys(this.statsLabel).reduce((_stats, _field_kr) => {
                    _stats[_field_kr] = _res_stats[this.statsLabel[_field_kr]] ?? 0;
                    return _stats;
                }, {});
                this.stats = {
                    total: Object.values(_fields).reduce((_total, _val) => _total + _val, 0),
                    fields: _fields,
                    colors: this.stats.colors
                };

                const _project_dates = _res?.info ?? {project_code: this.apiRequestProjectCode, target_end_date: null, start_date: null, end_date: null};
                const _format = (_date, _pattern) => !_date || _date === 'None' ? '미지정' : dayjs(_date).format(_pattern);
                // 영문·중문은 일·월 순서가 헷갈리지 않는 YYYY-MM-DD
                this.projectDates = {
                    ..._project_dates,
                    start_date_dis: _format(_project_dates.start_date, 'YYYY년 MM월 DD일'),
                    end_date_dis: _format(_project_dates.end_date, 'YYYY년 MM월 DD일'),
                    target_end_date_dis: _format(_project_dates.target_end_date, 'YYYY년 MM월 DD일'),
                    start_date_dis_en: _format(_project_dates.start_date, 'YYYY-MM-DD'),
                    end_date_dis_en: _format(_project_dates.end_date, 'YYYY-MM-DD'),
                    target_end_date_dis_en: _format(_project_dates.target_end_date, 'YYYY-MM-DD')
                };

                this.participants = this.transformParticipantsList(_res?.participant ?? []);
                this.appliedFilters = readContractorListQuery({}, PARTICIPANT_LIST_SPEC).filters;
                this.latest_search_params = {filter: 'all'};
                // "최근 조회 시간"은 통계까지 새로 받은 경우에만 바꾼다
                this.SET_CONTRACTOR_REQUEST_TIME();
                return true;
            } catch (error) {
                if (_seq !== this.listRequestSeq) return false;
                this.participants = [];
                this.appliedFilters = null;
                this.emitLoadError(error);
                return false;
            } finally {
                if (_seq === this.listRequestSeq) {
                    this.isLoadProjectData = false;
                    this.$emit('emit-loaded-status', true);
                }
            }
        },
        // URL -> 폼 복원(단방향). 표의 조건과 다르면 다시 조회하고, 페이지가 넘치거나 잘못된 키는 replace 로 보정
        async applyListQuery() {
            const {filters} = this.listQuery;
            this.participnatfilterForm = readContractorListQuery(this.$route.query, PARTICIPANT_LIST_SPEC).filters;
            if (hasListFilters(PARTICIPANT_LIST_SPEC, filters)) {
                this.collapsedParticipantFilterForm = true;
            }
            if (!isSameListFilters(PARTICIPANT_LIST_SPEC, filters, this.appliedFilters)) {
                await this.searchParticipants(filters);
            }
            // 조회가 실패했거나 그 사이 URL 이 다시 바뀌었으면 페이지 보정은 하지 않는다
            if (!isSameListFilters(PARTICIPANT_LIST_SPEC, filters, this.appliedFilters)) return;
            const _pages = Math.max(1, Math.ceil(this.participants.length / this.listQuery.perPage));
            await this.navigateList({currentPage: this.listQuery.currentPage > _pages ? 1 : this.listQuery.currentPage}, 'replace');
        },
        // 목록 상태를 URL 에 쓴다. 조건·표시 개수가 바뀌면 1페이지. URL 이 그대로면 false
        async navigateList(patch, method = 'push') {
            const _query = buildContractorListQuery(this.$route.query, PARTICIPANT_LIST_SPEC, {...this.listQuery, currentPage: 1, ...patch});
            if (isSameQuery(_query, this.$route.query)) return false;
            await this.$router[method]({name: this.$route.name, params: this.$route.params, query: _query}).catch(() => {});
            return true;
        },
        async searchParticipants(filters) {
            const _seq = ++this.listRequestSeq;
            const _condition = toSearchCondition(PARTICIPANT_LIST_SPEC, filters);
            const params = _condition ? {filter: 'search', condition: _condition} : {filter: 'all'};
            this.isParticipantSearching = true;
            try {
                const _res = await this.$projectsAxios({
                    method: 'get',
                    url: `/participant/${this.apiRequestProjectCode}`,
                    params: params
                });
                // 기다리는 사이 다른 요청이 시작됐거나 URL 조건이 바뀌었으면(뒤로가기) 이 응답은 버린다
                if (_seq !== this.listRequestSeq || !isSameListFilters(PARTICIPANT_LIST_SPEC, filters, this.listQuery.filters)) return;
                this.participants = this.transformParticipantsList(_res.data.list);
                this.appliedFilters = filters;
                this.latest_search_params = params;
            } catch (error) {
                if (_seq !== this.listRequestSeq || error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', this.$t('참여기업 조회'), this.$t('참여기업 리스트를 조회하지 못했습니다. 표시된 목록은 이전 조회 결과입니다. 다시 시도해주세요.'));
            } finally {
                if (_seq === this.listRequestSeq) {
                    this.isParticipantSearching = false;
                }
            }
        },
        // 조건을 URL 에 넣는다(watcher 가 조회). 주소가 이미 같으면 그 조건으로 다시 조회한다
        // 조회·다운로드 중에는 받지 않는다(이름 입력칸 Enter 는 비활성화되지 않으므로)
        async applyFilters(filters) {
            if (this.isParticipantsTableBusy) return;
            if (!(await this.navigateList({filters}))) {
                await this.searchParticipants(this.listQuery.filters);
            }
        },
        drawPieChart() {
            const data = {
                labels: Object.keys(this.stats.fields).map(_key => this.$t(_key)),
                datasets: [{
                    label: this.$t('진행현황 요약'),
                    data: this.stats.total !== 0 ? Object.values(this.stats.fields) : Object.values(this.stats.fields).map(() => 10),
                    backgroundColor: this.stats.total !== 0 ? Object.values(this.stats.colors) : '#CFD8DC',
                    hoverOffset: 4,
                    borderWidth: 3
                }]
            };

            const _stat_chart_options = {
                maintainAspectRatio: false,
                cutout: 20,
                plugins: {
                    datalabels: {
                        font: {
                            size: 15
                        },
                        formatter: (value) => {
                            if (!this.stats.total) {
                                return '0 %';
                            } else {
                                return value === 0
                                    ? '0%'
                                    : Math.round((value / this.stats.total) * 100) + ' %';
                            }
                        },
                        display: (context) => {
                            const _value = context.dataset.data[context.dataIndex];
                            if (this.stats.total === 0) {
                                return true;
                            } else {
                                if (_value === 0) {
                                    return false;
                                } else {
                                    const _share = Math.round((_value / this.stats.total) * 100);
                                    return _share > 10;
                                }
                            }
                        }
                    },
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                let label = context.label || '';
                                let _raw_value = this.stats.total === 0 ? 0 : context.parsed;
                                return ` ${label}: ${this.$t('{count}개 기업', {count: _raw_value})}`;
                            }
                        }
                    }
                }
            };

            if (!this.statsChartInstance) {
                this.statsChartInstance = this.$chartjs.createChart(
                    document.getElementById('progress-pie-chart'),
                    {type: 'doughnut', data, options: _stat_chart_options}
                );
            } else {
                this.$chartjs.updateChart(this.statsChartInstance, data, _stat_chart_options);
            }
        },
        // 범례: 그 상태 하나만 적용하고 조건 패널을 펼친다
        async viewParticipantByLegend(label_kr) {
            this.collapsedParticipantFilterForm = true;
            await this.applyFilters({status_code: [this.statsLabel[label_kr]]});
        },
        // 이름 검색: 적용된 조건에서 기업명만 바꾼다
        async searchParticipantByName() {
            await this.applyFilters({...this.appliedFilters, company_name: this.participnatfilterForm.company_name});
        },
        // 조건 패널 조회: 입력한 조건 전체를 적용한다
        async onSubmitParticipantFilter() {
            await this.applyFilters(this.participnatfilterForm);
        },
        // 검색 초기화(툴바 화살표·패널 초기화): 폼·URL 조건·페이지를 비우고 통계까지 다시 받는다
        async resetParticipantFilter() {
            if (this.isParticipantsTableBusy) return;
            this.participnatfilterForm = readContractorListQuery({}, PARTICIPANT_LIST_SPEC).filters;
            // 조회를 먼저 시작해(isLoadProjectData) watcher 가 중복 조회하지 않게 한 뒤 URL 을 비운다
            const _loading = this.loadProject();
            await this.navigateList({filters: {}, perPage: this.listQuery.perPage}, 'replace');
            await _loading;
        },
        // 표에 보이는 목록(마지막으로 적용된 조건)을 그대로 받는다
        async downloadParticipantsExcel() {
            try {
                this.isParticipantDownloading = true;

                const _res_participants_excel = await this.$projectsAxios({
                    method: 'get',
                    url: `/excel/${this.apiRequestProjectCode}`,
                    params: this.latest_search_params,
                    responseType: 'blob'
                });

                let fileName = '';
                switch (this.$i18n.locale) {
                case 'ko':
                    fileName = `${this.apiRequestProjectCode}_참여기업_진행현황_데이터.xlsx`;
                    break;
                case 'en':
                    fileName = `${this.apiRequestProjectCode}_participants_progress_data.xlsx`;
                    break;
                case 'ca':
                    fileName = `${this.apiRequestProjectCode}_参与企业_进展状态_数据.xlsx`;
                    break;
                default:
                    fileName = `${this.apiRequestProjectCode}_참여기업_진행현황_데이터.xlsx`;
                    break;
                }

                this.downloadFileFromResponse(_res_participants_excel, fileName);

            } catch (error) {
                if (error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', this.$t('현재 목록 엑셀 다운로드'), this.$t('엑셀 다운로드 중 오류가 발생했습니다. 다시 시도해주세요.'));
            } finally {
                this.isParticipantDownloading = false;
            }

        }
    }
};
</script>

<style>

</style>
